import type { ReactNode } from "react";
import type {
  ByInvitationScreenState,
  CreatedScreenState,
  InviteExpiredScreenState,
  PortalWording,
  RefusedScreenState,
  RequestSentScreenState,
  SignedOutScreenState,
  WayRefusedScreenState,
} from "@shared-core/domain/portal-sign-in/types";
import { Button } from "../primitives/button";
import { Card } from "../primitives/card";
import { Notice, SignInFrame } from "./SignInFrame";
import { WayButton } from "./WayButton";

interface WordingProps {
  wording: PortalWording;
}

/** The card's lead line, in body size. */
function Lead({ children }: { children: ReactNode }) {
  return <p className="text-sm">{children}</p>;
}

/** What follows the lead line: smaller and quieter. */
function Aside({ children }: { children: ReactNode }) {
  return <p className="text-[13px] text-muted-foreground">{children}</p>;
}

/**
 * *Use another address*: the way out of every screen that turned an address away. Outlined when it is the only thing the
 * card offers; a quiet text button when the card has a main button or ways above it.
 */
function UseAnotherAddress({
  onClick,
  quiet = false,
}: {
  onClick(): void;
  quiet?: boolean;
}) {
  return (
    <Button
      variant={quiet ? "ghost" : "outline"}
      className={
        quiet
          ? "h-10 w-full font-medium text-muted-foreground"
          : "h-10 w-full font-semibold"
      }
      onClick={onClick}
    >
      Use another address
    </Button>
  );
}

export function CreatedScreen({
  state,
  wording,
  busy = false,
  onContinue,
}: WordingProps & {
  state: CreatedScreenState;
  busy?: boolean;
  onContinue(): void;
}) {
  return (
    <SignInFrame
      eyebrow={state.partyName}
      title="Your account has been created"
    >
      <Card className="gap-3 p-5">
        <Notice tone="ok">
          <b>{state.address}</b> can now use the portal for {state.partyName}.
        </Notice>
        <Aside>
          To start with, you&apos;ll see your own {wording.records}.{" "}
          {wording.company} can give you access to more.
        </Aside>
        <Button
          className="h-10 w-full font-semibold"
          disabled={busy}
          onClick={onContinue}
        >
          Go to the portal
        </Button>
      </Card>
    </SignInFrame>
  );
}

export function ByInvitationScreen({
  state,
  wording,
  busy = false,
  onAskForAccess,
  onUseAnotherAddress,
}: WordingProps & {
  state: ByInvitationScreenState;
  busy?: boolean;
  onAskForAccess(): void;
  onUseAnotherAddress(): void;
}) {
  if (state.partyName === null) {
    return (
      <SignInFrame eyebrow={wording.brand} title="The portal is by invitation">
        <Card className="gap-3 p-5">
          <Lead>
            To use the {wording.company} portal, <b>{state.address}</b> needs an
            invite.
          </Lead>
          <Aside>
            If you order from {wording.company}, ask your contact here, or call
            us on {wording.phone}. If you use another address with us, sign in
            with that address.
          </Aside>
          <UseAnotherAddress onClick={onUseAnotherAddress} />
        </Card>
      </SignInFrame>
    );
  }
  const askedWhen =
    state.askedOn === "today" ? "today" : `on ${state.askedOn ?? ""}`;
  return (
    <SignInFrame
      eyebrow={state.partyName}
      title={`The portal for ${state.partyName} is by invitation`}
    >
      <Card className="gap-3 p-5">
        <Lead>
          Ask for access and we&apos;ll email an invite to{" "}
          <b>{state.address}</b> once your request is agreed. That usually takes
          a working day.
        </Lead>
        {state.askedOn ? (
          <Notice tone="info">
            You asked {askedWhen}. We&apos;ll email you when someone has looked
            at your request.
          </Notice>
        ) : (
          <Button
            className="h-10 w-full font-semibold"
            disabled={busy}
            onClick={onAskForAccess}
          >
            Ask for access
          </Button>
        )}
        <UseAnotherAddress quiet onClick={onUseAnotherAddress} />
      </Card>
    </SignInFrame>
  );
}

export function InviteExpiredScreen({
  state,
  busy = false,
  onAskForAccess,
}: WordingProps & {
  state: InviteExpiredScreenState;
  busy?: boolean;
  onAskForAccess(): void;
}) {
  return (
    <SignInFrame eyebrow={state.partyName} title="This invite has expired">
      <Card className="gap-3 p-5">
        <Lead>
          Invites last 30 days. The invite for <b>{state.address}</b> ended on{" "}
          {state.endedOn}.
        </Lead>
        <Aside>
          Ask for access again, and we&apos;ll email a new invite once your
          request is agreed.
        </Aside>
        <Button
          className="h-10 w-full font-semibold"
          disabled={busy}
          onClick={onAskForAccess}
        >
          Ask for access again
        </Button>
      </Card>
    </SignInFrame>
  );
}

export function RequestSentScreen({
  state,
  wording,
}: WordingProps & { state: RequestSentScreenState }) {
  return (
    <SignInFrame eyebrow={state.partyName} title="Request sent">
      <Card className="gap-3 p-5">
        <Lead>
          We&apos;ve asked {wording.company} to invite <b>{state.address}</b> to
          the portal for {state.partyName}.
        </Lead>
        <Aside>
          We&apos;ll email you when someone has looked at your request, usually
          within a working day.
        </Aside>
      </Card>
    </SignInFrame>
  );
}

export function RefusedScreen({
  state,
  wording,
  onUseAnotherAddress,
}: WordingProps & {
  state: RefusedScreenState;
  onUseAnotherAddress(): void;
}) {
  return (
    <SignInFrame eyebrow={wording.brand} title={"Sorry, you can’t sign in"}>
      <Card className="gap-3 p-5">
        <Lead>
          {state.address} can no longer use the portal. If that&apos;s a
          mistake, call us on {wording.phone}.
        </Lead>
        <UseAnotherAddress onClick={onUseAnotherAddress} />
      </Card>
    </SignInFrame>
  );
}

export function WayRefusedScreen({
  state,
  wording,
  busy = false,
  onProvider,
  onEmailCode,
  onUseAnotherAddress,
}: WordingProps & {
  state: WayRefusedScreenState;
  busy?: boolean;
  onProvider(): void;
  onEmailCode(address: string): void;
  onUseAnotherAddress(): void;
}) {
  const { partyName, address, waysThatWork } = state;
  const reason =
    state.problem === "personal_microsoft"
      ? `${partyName} doesn't allow personal Microsoft accounts.`
      : `${partyName} doesn't allow Microsoft accounts from other organisations.`;
  const hasOtherWay = waysThatWork.microsoft || waysThatWork.code;
  return (
    <SignInFrame eyebrow={partyName} title={"That account can’t be used here"}>
      <Card className="gap-3 p-5">
        <Notice tone="warn">
          {hasOtherWay
            ? reason
            : `${reason} ${address} has no other way to sign in at ${partyName}. Ask whoever manages ${partyName}'s portal users, or call ${wording.company} on ${wording.phone}.`}
        </Notice>
        {waysThatWork.microsoft ? (
          <WayButton
            way="microsoft"
            isLastUsed={false}
            disabled={busy}
            onClick={onProvider}
          >
            Continue with Microsoft
          </WayButton>
        ) : null}
        {waysThatWork.code ? (
          <WayButton
            way="code"
            isLastUsed={false}
            disabled={busy}
            onClick={() => onEmailCode(address)}
          >
            Email a code to {address}
          </WayButton>
        ) : null}
        <UseAnotherAddress quiet onClick={onUseAnotherAddress} />
      </Card>
    </SignInFrame>
  );
}

export function SignedOutScreen({
  state,
  wording,
  onSignInAgain,
}: WordingProps & {
  state: SignedOutScreenState;
  onSignInAgain(): void;
}) {
  return (
    <SignInFrame
      eyebrow={wording.brand}
      title={state.everywhere ? "Signed out everywhere" : "Signed out"}
    >
      <Card className="gap-3 p-5">
        <Lead>
          {state.everywhere
            ? "Every browser and device signed in as this address has been signed out."
            : "You’ve signed out on this browser."}
        </Lead>
        <Button className="h-10 w-full font-semibold" onClick={onSignInAgain}>
          Sign in again
        </Button>
      </Card>
    </SignInFrame>
  );
}
